import { SAMPLES_VERSION, SOUND_TAKES, soundUrl, type Ambience, type MusicTags } from "../shared/story";
import { useSamples } from "./instruments";
import { Sampler } from "./sampler";
import { Score, type Mood } from "./score";

/**
 * Ambient sound, in two layers. The score (score.ts): generative music, always there under the story, following its
 * tension. The place: when the scene moves somewhere, its soundscape plays one full pass to set it, then fades and
 * leaves the score (with the score off, the place stays). Each soundscape plays one of SOUND_TAKES
 * recorded loops (Stable Audio 3, shared R2 library), a different take picked each time the scene comes back; while a
 * take downloads the old scene keeps playing, and if it can't be fetched or decoded the soundscape is synthesized in
 * the browser instead (the recipes below). Off until the player turns it on (browsers only start audio after a tap).
 */

type Ctx = BaseAudioContext;
interface Voice { out: GainNode; stop(): void }

const noiseCache = new WeakMap<Ctx, Record<string, AudioBuffer>>();
/** A few seconds of white, pink or brown noise, made once per context. */
function noise(ctx: Ctx, color: "white" | "pink" | "brown"): AudioBuffer {
  const cache = noiseCache.get(ctx) ?? {};
  noiseCache.set(ctx, cache);
  if (cache[color]) return cache[color]!;
  const len = ctx.sampleRate * 4;
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  let b0 = 0, b1 = 0, b2 = 0, last = 0;
  for (let i = 0; i < len; i++) {
    const w = Math.random() * 2 - 1;
    if (color === "white") d[i] = w * 0.5;
    else if (color === "pink") { b0 = 0.99765 * b0 + w * 0.099; b1 = 0.963 * b1 + w * 0.2965; b2 = 0.57 * b2 + w * 1.0527; d[i] = (b0 + b1 + b2 + w * 0.1848) * 0.11; }
    else { last = (last + 0.02 * w) / 1.02; d[i] = last * 3.2; }
  }
  return (cache[color] = buf);
}

function loopNoise(ctx: Ctx, color: "white" | "pink" | "brown"): AudioBufferSourceNode {
  const src = ctx.createBufferSource();
  src.buffer = noise(ctx, color);
  src.loop = true;
  src.loopStart = Math.random() * 2; // decorrelate layers that share a buffer
  return src;
}
const filter = (ctx: Ctx, type: BiquadFilterType, freq: number, q = 0.7) => { const f = ctx.createBiquadFilter(); f.type = type; f.frequency.value = freq; f.Q.value = q; return f; };
const gain = (ctx: Ctx, v: number) => { const g = ctx.createGain(); g.gain.value = v; return g; };
/** A slow wobble on an AudioParam (sine LFO): depth around its current value. */
function wobble(ctx: Ctx, param: AudioParam, rate: number, depth: number): OscillatorNode {
  const o = ctx.createOscillator(); o.frequency.value = rate;
  const g = gain(ctx, depth); o.connect(g); g.connect(param); o.start();
  return o;
}

/** Runs `tick(at)` for events scheduled a little ahead of time; `every` returns the gap to the next event in seconds. */
function scheduler(ctx: Ctx, every: () => number, tick: (at: number) => void, horizon = 1.5): () => void {
  let next = ctx.currentTime + 0.1;
  let alive = true;
  const fill = () => { while (alive && next < ctx.currentTime + horizon) { tick(next); next += every(); } };
  fill();
  // An OfflineAudioContext never advances in real time: schedule its whole render up front.
  if (!("close" in ctx)) { const end = (ctx as OfflineAudioContext).length / ctx.sampleRate; while (next < end) { tick(next); next += every(); } return () => { alive = false; }; }
  const t = setInterval(fill, 250);
  return () => { alive = false; clearInterval(t); };
}

/** A short burst of filtered noise: a drip, a click, a clack, a crackle. */
function burst(ctx: Ctx, out: AudioNode, at: number, o: { freq: number; q?: number; dur: number; level: number; type?: BiquadFilterType; color?: "white" | "pink" | "brown" }) {
  const src = ctx.createBufferSource(); src.buffer = noise(ctx, o.color ?? "white");
  const f = filter(ctx, o.type ?? "bandpass", o.freq, o.q ?? 3);
  const g = gain(ctx, 0);
  g.gain.setValueAtTime(0, at); g.gain.linearRampToValueAtTime(o.level, at + 0.004); g.gain.exponentialRampToValueAtTime(0.0001, at + o.dur);
  src.connect(f); f.connect(g); g.connect(out);
  src.start(at, Math.random() * 3); src.stop(at + o.dur + 0.05);
}
/** A short tone: a cup's clink, a cricket's chirp, a piano note, a bass note, a bell. */
function tone(ctx: Ctx, out: AudioNode, at: number, o: { freq: number; dur: number; level: number; type?: OscillatorType; attack?: number; glide?: number }) {
  const osc = ctx.createOscillator(); osc.type = o.type ?? "sine"; osc.frequency.setValueAtTime(o.freq, at);
  if (o.glide) osc.frequency.exponentialRampToValueAtTime(o.freq * o.glide, at + o.dur);
  const g = gain(ctx, 0);
  g.gain.setValueAtTime(0, at); g.gain.linearRampToValueAtTime(o.level, at + (o.attack ?? 0.005)); g.gain.exponentialRampToValueAtTime(0.0001, at + o.dur);
  osc.connect(g); g.connect(out); osc.start(at); osc.stop(at + o.dur + 0.05);
}
/** A small room (a generated impulse response) for interiors and caves. */
function room(ctx: Ctx, seconds: number, decay: number): ConvolverNode {
  const len = Math.floor(ctx.sampleRate * seconds);
  const ir = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let c = 0; c < 2; c++) { const d = ir.getChannelData(c); for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay); }
  const conv = ctx.createConvolver(); conv.buffer = ir; return conv;
}
const rand = (a: number, b: number) => a + Math.random() * (b - a);

type Recipe = (ctx: Ctx, out: GainNode) => () => void;

const RECIPES: Record<Exclude<Ambience, "silence">, Recipe> = {
  rain: (ctx, out) => {
    const bed = loopNoise(ctx, "pink"); const hp = filter(ctx, "highpass", 500); const lp = filter(ctx, "lowpass", 7000); const g = gain(ctx, 0.55);
    bed.connect(hp); hp.connect(lp); lp.connect(g); g.connect(out); bed.start();
    const w = wobble(ctx, g.gain, 0.07, 0.12);
    const stop = scheduler(ctx, () => rand(0.03, 0.18), (at) => burst(ctx, out, at, { freq: rand(2500, 6000), dur: rand(0.02, 0.06), level: rand(0.05, 0.16) }));
    return () => { stop(); bed.stop(); w.stop(); };
  },
  wind: (ctx, out) => {
    const src = loopNoise(ctx, "brown"); const bp = filter(ctx, "bandpass", 420, 1.4); const g = gain(ctx, 0.9);
    src.connect(bp); bp.connect(g); g.connect(out); src.start();
    const a = wobble(ctx, bp.frequency, 0.05, 260); const b = wobble(ctx, g.gain, 0.09, 0.45);
    return () => { src.stop(); a.stop(); b.stop(); };
  },
  sea: (ctx, out) => {
    const src = loopNoise(ctx, "brown"); const lp = filter(ctx, "lowpass", 900); const g = gain(ctx, 0.7);
    src.connect(lp); lp.connect(g); g.connect(out); src.start();
    const a = wobble(ctx, g.gain, 0.11, 0.55); const b = wobble(ctx, lp.frequency, 0.11, 500);
    const hiss = loopNoise(ctx, "pink"); const hp = filter(ctx, "highpass", 3000); const hg = gain(ctx, 0.08); hiss.connect(hp); hp.connect(hg); hg.connect(out); hiss.start();
    const c = wobble(ctx, hg.gain, 0.11, 0.07);
    return () => { src.stop(); hiss.stop(); a.stop(); b.stop(); c.stop(); };
  },
  train: (ctx, out) => {
    const rumble = loopNoise(ctx, "brown"); const lp = filter(ctx, "lowpass", 160); const g = gain(ctx, 1.1);
    rumble.connect(lp); lp.connect(g); g.connect(out); rumble.start();
    const sway = wobble(ctx, g.gain, 0.25, 0.25);
    // Wheels over rail joints: a pair of clacks, then the next bogie.
    const pattern = [0.0, 0.13, 0.95, 1.08]; let k = 0;
    const stop = scheduler(ctx, () => { const gap = k % 4 === 3 ? 2.2 - 1.08 : pattern[(k + 1) % 4]! - pattern[k % 4]!; k++; return gap; },
      (at) => burst(ctx, out, at, { freq: rand(700, 900), q: 6, dur: 0.09, level: 0.35, color: "pink" }));
    return () => { stop(); rumble.stop(); sway.stop(); };
  },
  city: (ctx, out) => {
    const hum = loopNoise(ctx, "brown"); const lp = filter(ctx, "lowpass", 380); const g = gain(ctx, 0.9);
    hum.connect(lp); lp.connect(g); g.connect(out); hum.start();
    const mid = loopNoise(ctx, "pink"); const bp = filter(ctx, "bandpass", 1200, 0.6); const mg = gain(ctx, 0.12); mid.connect(bp); bp.connect(mg); mg.connect(out); mid.start();
    const w = wobble(ctx, g.gain, 0.03, 0.2);
    const stop = scheduler(ctx, () => rand(4, 11), (at) => { const f = rand(320, 460); tone(ctx, out, at, { freq: f, dur: rand(0.4, 0.9), level: 0.035, type: "sawtooth", attack: 0.05 }); tone(ctx, out, at, { freq: f * 1.26, dur: rand(0.4, 0.9), level: 0.025, type: "sawtooth", attack: 0.05 }); });
    return () => { stop(); hum.stop(); mid.stop(); w.stop(); };
  },
  cafe: (ctx, out) => {
    const murmur = loopNoise(ctx, "pink"); const bp = filter(ctx, "bandpass", 700, 0.9); const g = gain(ctx, 0.45);
    murmur.connect(bp); bp.connect(g); g.connect(out); murmur.start();
    const a = wobble(ctx, g.gain, 0.6, 0.15); const b = wobble(ctx, bp.frequency, 0.4, 250);
    const stop = scheduler(ctx, () => rand(0.8, 3.5), (at) => tone(ctx, out, at, { freq: rand(2200, 3800), dur: rand(0.15, 0.4), level: rand(0.03, 0.07) }));
    return () => { stop(); murmur.stop(); a.stop(); b.stop(); };
  },
  jazz: (ctx, out) => {
    // A small generative trio, swung, in F: walking bass, ride cymbal, soft comping on 2 and 4.
    const beat = 60 / 116;
    const chords = [[55, 58.27, 69.3, 87.31], [65.41, 82.41, 87.31, 116.54], [43.65, 55, 65.41, 82.41], [73.42, 92.5, 110, 130.81]]; // Gm7 C7 Fmaj7 D7 (roots in Hz, bass range)
    const reverb = room(ctx, 1.6, 3); const wet = gain(ctx, 0.25); reverb.connect(wet); wet.connect(out);
    const bus = gain(ctx, 1); bus.connect(out); bus.connect(reverb);
    let step = 0;
    const stop = scheduler(ctx, () => beat, (at) => {
      const bar = Math.floor(step / 4) % 4, b = step % 4, ch = chords[bar]!;
      const note = b === 3 ? ch[0]! * 1.06 : ch[[0, 2, 1, 3][b]!]!; // walk toward the next root
      tone(ctx, bus, at, { freq: note, dur: beat * 0.95, level: 0.22, type: "triangle", attack: 0.01 });
      burst(ctx, bus, at, { freq: 8000, type: "highpass", q: 0.7, dur: 0.35, level: 0.05 });
      if (b === 1 || b === 3) burst(ctx, bus, at + beat * 0.66, { freq: 8000, type: "highpass", q: 0.7, dur: 0.18, level: 0.03 }); // swung skip
      if (b === 1 || b === 3) for (const f of ch.slice(1)) tone(ctx, bus, at + rand(0, 0.02), { freq: f * 4, dur: 0.45, level: 0.035, type: "sine", attack: 0.008 });
      step++;
    });
    return () => stop();
  },
  fire: (ctx, out) => {
    const roar = loopNoise(ctx, "brown"); const lp = filter(ctx, "lowpass", 600); const g = gain(ctx, 0.6);
    roar.connect(lp); lp.connect(g); g.connect(out); roar.start();
    const w = wobble(ctx, g.gain, 0.3, 0.2);
    const stop = scheduler(ctx, () => rand(0.02, 0.25), (at) => burst(ctx, out, at, { freq: rand(1500, 5000), q: 1.5, dur: rand(0.005, 0.03), level: rand(0.05, 0.25) }));
    return () => { stop(); roar.stop(); w.stop(); };
  },
  night: (ctx, out) => {
    const air = loopNoise(ctx, "brown"); const lp = filter(ctx, "lowpass", 500); const g = gain(ctx, 0.25);
    air.connect(lp); lp.connect(g); g.connect(out); air.start();
    // Crickets: bursts of quick chirps at ~4.4 kHz.
    const stop = scheduler(ctx, () => rand(0.6, 1.4), (at) => { const n = 3 + Math.floor(Math.random() * 3); for (let i = 0; i < n; i++) tone(ctx, out, at + i * 0.05, { freq: 4400 + rand(-80, 80), dur: 0.035, level: 0.04 }); });
    return () => { stop(); air.stop(); };
  },
  house: (ctx, out) => {
    const tone_ = loopNoise(ctx, "brown"); const lp = filter(ctx, "lowpass", 140); const g = gain(ctx, 0.35);
    tone_.connect(lp); lp.connect(g); g.connect(out); tone_.start();
    const reverb = room(ctx, 1.2, 4); const wet = gain(ctx, 0.5); reverb.connect(wet); wet.connect(out);
    const stopTick = scheduler(ctx, () => 1, (at) => burst(ctx, reverb, at, { freq: 3000, q: 8, dur: 0.02, level: 0.06 }));
    const stopCreak = scheduler(ctx, () => rand(6, 14), (at) => tone(ctx, reverb, at, { freq: rand(180, 260), dur: rand(0.5, 1.1), level: 0.05, type: "sawtooth", attack: 0.2, glide: rand(0.8, 1.2) }));
    return () => { stopTick(); stopCreak(); tone_.stop(); };
  },
  clock: (ctx, out) => {
    let k = 0;
    const stop = scheduler(ctx, () => 1, (at) => burst(ctx, out, at, { freq: k++ % 2 ? 2600 : 3300, q: 10, dur: 0.03, level: 0.2 }));
    const room_ = loopNoise(ctx, "brown"); const lp = filter(ctx, "lowpass", 120); const g = gain(ctx, 0.2); room_.connect(lp); lp.connect(g); g.connect(out); room_.start();
    return () => { stop(); room_.stop(); };
  },
  crowd: (ctx, out) => {
    const murmur = loopNoise(ctx, "pink"); const bp = filter(ctx, "bandpass", 550, 0.7); const g = gain(ctx, 0.7);
    murmur.connect(bp); bp.connect(g); g.connect(out); murmur.start();
    const a = wobble(ctx, g.gain, 0.9, 0.2); const b = wobble(ctx, bp.frequency, 0.7, 200);
    const stop = scheduler(ctx, () => rand(5, 12), (at) => tone(ctx, out, at, { freq: rand(600, 900), dur: 1.6, level: 0.035, attack: 0.01 }));
    return () => { stop(); murmur.stop(); a.stop(); b.stop(); };
  },
  water: (ctx, out) => {
    const reverb = room(ctx, 2.2, 2.5); const wet = gain(ctx, 0.7); reverb.connect(wet); wet.connect(out);
    const bed = loopNoise(ctx, "brown"); const lp = filter(ctx, "lowpass", 200); const g = gain(ctx, 0.2); bed.connect(lp); lp.connect(g); g.connect(out); bed.start();
    const stop = scheduler(ctx, () => rand(0.4, 2.2), (at) => tone(ctx, reverb, at, { freq: rand(900, 1800), dur: 0.12, level: 0.12, glide: 1.6 }));
    return () => { stop(); bed.stop(); };
  },
};

/** Builds one soundscape on a context, into `dest`; returns its gain (for fades) and a stop. */
/** Level trims so every soundscape sits near the same loudness (measured offline: RMS about 0.08 each). */
const TRIM: Record<Exclude<Ambience, "silence">, number> = {
  rain: 1, wind: 0.95, sea: 0.4, train: 0.45, city: 0.45, cafe: 2.2, jazz: 2, fire: 0.7, night: 1.6, house: 1.4, clock: 2.2, crowd: 1.35, water: 2.3,
};

export function voice(ctx: Ctx, key: Exclude<Ambience, "silence">, dest: AudioNode): Voice {
  const out = gain(ctx, 0);
  const trim = gain(ctx, TRIM[key]);
  out.connect(trim); trim.connect(dest);
  const stop = RECIPES[key](ctx, out);
  return { out, stop: () => { try { stop(); } catch { /* already stopped */ } out.disconnect(); trim.disconnect(); } };
}
export const SOUNDSCAPES = Object.keys(RECIPES) as Exclude<Ambience, "silence">[];

const FADE = 2.5;
/** How long the place takes to fade under the score once its pass is over. */
const ESTABLISH_FADE = 12;
const LEVEL = 0.32;

/** The page's one sound engine: on/off, volume, and a cross-fade to whatever the scene sounds like. */
type Scape = Exclude<Ambience, "silence">;
/** Give up on a download after this long and synthesize the soundscape instead. */
const LOAD_TIMEOUT_MS = 12000;
/** Decoded loops kept in memory (about 22 MB each: 64 s of stereo float). */
const KEEP_DECODED = 2;

/** A recorded loop as a voice: looped seamlessly, entering at a random point so a returning scene doesn't restart. */
function recordedVoice(ctx: Ctx, buf: AudioBuffer, dest: AudioNode): Voice {
  const out = gain(ctx, 0);
  out.connect(dest);
  const src = ctx.createBufferSource();
  src.buffer = buf; src.loop = true;
  src.connect(out);
  src.start(0, Math.random() * buf.duration);
  return { out, stop: () => { try { src.stop(); } catch { /* already stopped */ } src.disconnect(); out.disconnect(); } };
}

export class SoundEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private current: { key: Ambience; v: Voice | null } = { key: "silence", v: null };
  private wanted: Ambience = "silence";
  /** The soundscape being moved to: `current` once it plays, or one whose loop is still downloading. */
  private target: Ambience = "silence";
  /** Bumped on every scene change, so a slow download for an earlier scene never starts playing. */
  private ticket = 0;
  private decoded = new Map<string, Promise<AudioBuffer>>();
  private lastTake: Partial<Record<Scape, number>> = {};
  /** Set after a failed download: the rest of the session synthesizes instead of retrying. */
  private offline = false;
  private score: Score | null = null;
  private pump: number | undefined;
  private mood: Mood | null = null;
  private musicTags: MusicTags | null = null;
  private musicOn = true;
  /** The recorded instruments: loaded the first time sound is turned on; until a set is ready its voice synthesizes. */
  private sampler: Sampler | null = null;
  /** The story has ended: the coda played and the score stays silent (until another story, or music back on). */
  private finished = false;
  on = false;

  /** Must be called from a tap (browsers allow audio to start only then). */
  enable(): void {
    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.master = gain(this.ctx, LEVEL);
      this.master.connect(this.ctx.destination);
    }
    if (!this.sampler) {
      this.sampler = new Sampler({ base: `/api/samples/${SAMPLES_VERSION}/` });
      useSamples(this.sampler);
      void this.sampler.init().then((ok) => { if (ok) void this.score?.prefetch(); });
    }
    void this.ctx.resume();
    this.on = true;
    this.play(this.wanted, true);
    this.runScore();
  }
  disable(): void {
    this.on = false;
    this.ticket++;
    this.target = "silence";
    this.fadeOut(this.current.v);
    this.current = { key: "silence", v: null };
    this.runScore();
  }

  /** The story's tension (null on the home screen: no music there). */
  setMood(m: Mood | null): void {
    this.mood = m;
    if (m) this.score?.setMood(m);
    this.runScore();
  }
  /** The scene's music tags (instruments and feel), from the canon pass. */
  setTags(t: MusicTags | null): void { this.musicTags = t; this.score?.setTags(t); }
  /** A sudden event in the story. */
  accent(): void { if (this.on && this.musicOn) this.score?.accent(); }
  /** A chapter ended: the music fades into silence (then comes back with the next chapter). */
  closeChapter(): void { if (this.on && this.musicOn) this.score?.closeChapter(); }
  /** The story ended: a coda, then silence. */
  endStory(): void {
    if (this.finished) return;
    this.finished = true;
    if (this.on && this.musicOn) this.score?.coda();
    if (this.pump !== undefined) { window.clearInterval(this.pump); this.pump = undefined; }
  }
  /** Another story is open: its music may play again. */
  storyOpened(): void { if (this.finished) { this.finished = false; this.runScore(); } }
  /** The score on or off (the places then stay, as before). */
  music(on: boolean): void {
    this.musicOn = on;
    if (on) this.finished = false;
    const v = this.current.v;
    if (!on && v && this.ctx) { v.out.gain.cancelScheduledValues(this.ctx.currentTime); v.out.gain.setTargetAtTime(1, this.ctx.currentTime, 1.5); }
    this.runScore();
  }
  get musicPlaying() { return !!this.score?.playing; }

  /** Starts or stops the score to match: sound on, music on, in a story. A timer keeps it written 1.5 s ahead. */
  private runScore(): void {
    const ctx = this.ctx, master = this.master;
    const want = this.on && this.musicOn && !this.finished && !!this.mood && !!ctx && !!master;
    if (want && ctx && master) {
      if (!this.score) { this.score = new Score(ctx, master); this.score.setScene(this.wanted); this.score.setTags(this.musicTags); this.score.setMood(this.mood!, true); }
      if (!this.score.playing) this.score.start(ctx.currentTime + 0.1);
      if (this.pump === undefined) {
        const tick = () => this.score?.schedule(ctx.currentTime + 1.5);
        tick();
        this.pump = window.setInterval(tick, 250);
      }
    } else {
      if (this.score?.playing && ctx) this.score.stop(ctx.currentTime);
      if (this.pump !== undefined) { window.clearInterval(this.pump); this.pump = undefined; }
    }
  }
  volume(v: number): void { if (this.master && this.ctx) this.master.gain.setTargetAtTime(LEVEL * v, this.ctx.currentTime, 0.1); }

  /** The scene's soundscape: cross-fades to it if sound is on (remembered for when it's turned on). */
  play(key: Ambience, force = false): void {
    this.wanted = key;
    if (key !== this.target || force) this.score?.setScene(key);
    if (!this.on || !this.ctx || !this.master || (key === this.target && !force)) return;
    this.target = key;
    const ticket = ++this.ticket;
    if (key === "silence") { this.swap(key, null); return; }
    if (this.offline) { this.swap(key, voice(this.ctx, key, this.master)); return; }
    this.load(key, this.pickTake(key)).then(
      (buf) => { if (ticket === this.ticket && this.ctx && this.master) this.swap(key, recordedVoice(this.ctx, buf, this.master), buf.duration); },
      () => { this.offline = true; if (ticket === this.ticket && this.ctx && this.master) this.swap(key, voice(this.ctx, key, this.master)); },
    );
  }

  /** A take other than the one this soundscape played last time. */
  private pickTake(key: Scape): number {
    const last = this.lastTake[key];
    let n = Math.floor(Math.random() * SOUND_TAKES);
    if (SOUND_TAKES > 1 && n === last) n = (n + 1 + Math.floor(Math.random() * (SOUND_TAKES - 1))) % SOUND_TAKES;
    this.lastTake[key] = n;
    return n;
  }

  private load(key: Scape, take: number): Promise<AudioBuffer> {
    const url = soundUrl(key, take);
    const hit = this.decoded.get(url);
    if (hit) { this.decoded.delete(url); this.decoded.set(url, hit); return hit; }
    const ctx = this.ctx!;
    const p = new Promise<AudioBuffer>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error("sound: timed out")), LOAD_TIMEOUT_MS);
      fetch(url)
        .then((r) => { if (!r.ok) throw new Error(`sound: ${r.status}`); return r.arrayBuffer(); })
        .then((bytes) => ctx.decodeAudioData(bytes))
        .then(resolve, reject)
        .finally(() => clearTimeout(timer));
    });
    p.catch(() => this.decoded.delete(url));
    this.decoded.set(url, p);
    while (this.decoded.size > KEEP_DECODED) this.decoded.delete(this.decoded.keys().next().value!);
    return p;
  }

  private swap(key: Ambience, v: Voice | null, pass = 64): void {
    this.fadeOut(this.current.v);
    if (v && this.ctx) {
      const t = this.ctx.currentTime;
      v.out.gain.setValueAtTime(0, t);
      v.out.gain.linearRampToValueAtTime(1, t + FADE);
      // With the score playing, the place sets the scene for one pass, then leaves it to the music.
      if (this.musicOn && this.mood) v.out.gain.setTargetAtTime(0, t + pass, ESTABLISH_FADE / 3);
    }
    this.current = { key, v };
  }

  private fadeOut(v: Voice | null): void {
    if (!v || !this.ctx) return;
    const t = this.ctx.currentTime;
    v.out.gain.cancelScheduledValues(t);
    v.out.gain.setValueAtTime(v.out.gain.value, t);
    v.out.gain.linearRampToValueAtTime(0, t + FADE);
    setTimeout(() => v.stop(), FADE * 1000 + 100);
  }
}

export const sound = new SoundEngine();
